import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {getAccessToken, getRefreshToken, setAccessToken} from './tokenStore';
import {AppDispatch} from "../utils/store";
import config from "../config/config";
// Removed: import {useNavigate} from "react-router-dom";
import {clearUserData} from '../utils/userSlice'; // Import clearUserData action
import {isAccountFrozenError, markAccountFrozen} from './accountFrozen';

const BASE_URL = config.apiUrl

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    _retry?: boolean;
    /**
     * The session that issued the request: its refresh token at send time (it is
     * stable across access-token refreshes and replaced by every login/logout).
     * A late response from a previous session must never log out — or hand a
     * token to — the session that is current now.
     */
    _session?: string;
}

export class ApiService {
    private static instance: ApiService;
    // Removed: private navigate = useNavigate(); // Cannot use hooks here

    api: AxiosInstance
    private isRefreshing: boolean = false;
    private failedQueue: Array<{
        resolve: (token?: string) => void,
        reject: (err: unknown) => void
    }> = [];
    private pendingAuthEvents: Array<{ eventType: string; errorMessage?: string }> = [];

    private constructor(private dispatch: AppDispatch) { // Keep dispatch if needed for other actions
        this.api = axios.create({
            baseURL: BASE_URL,
        });

        this.initInterceptors()
    }

    public static getInstance(dispatch: AppDispatch): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService(dispatch);
        }

        return ApiService.instance;
    }

    // Accessor for non-React callers (e.g. jobService) that need the
    // authenticated axios instance but can't use the useApiService() hook.
    // The provider constructs the singleton at mount, so by the time any
    // job runs it always exists; throw loudly if that invariant is broken.
    public static current(): ApiService {
        if (!ApiService.instance) {
            throw new Error('ApiService has not been initialised yet (ApiServiceProvider must mount first).');
        }
        return ApiService.instance;
    }

    processQueue = (error: unknown, token: string | null = null) => {
        this.failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token ?? undefined);
            }
        });

        this.failedQueue = [];
    };

    // Renamed from clearUserData to avoid conflict with imported action
    handleLogoutAndRedirect = () => {
        // Dispatch the logout action
        clearUserData(this.dispatch);
        // Navigation should happen in the UI layer after logout is dispatched
        // For instance, using a useEffect watching the isLoggedIn state.
        // Removed: this.navigate('/login'); // Cannot use navigate here
    }

    /** True while the session that sent `config` is still the logged-in one. */
    private isCurrentSession = (config: CustomAxiosRequestConfig | undefined): boolean =>
        (config?._session ?? undefined) === getRefreshToken();

    /**
     * The account was frozen: final, so never refresh — log out ONCE and let the
     * login screen say why. Only the session that sent the request is logged
     * out: concurrent frozen responses find it already gone, and a late one from
     * an older session must not end the session that logged in since.
     */
    private handleAccountFrozen = (session: string | undefined) => {
        if (session !== getRefreshToken()) return;
        // Only an access token left (the refresh cookie outlived its 7 days while
        // the access cookie kept being re-set): still log out, once.
        if (session === undefined && !getAccessToken()) return;
        markAccountFrozen();
        this.handleLogoutAndRedirect();
    }

    private flushPendingAuthEvents(): void {
        if (this.pendingAuthEvents.length === 0) return;
        const events = [...this.pendingAuthEvents];
        this.pendingAuthEvents = [];
        for (const event of events) {
            this.api.post('/stats/log', event).catch(() => {});
        }
    }

    initInterceptors() {
        this.api.interceptors.request.use(config => {
            (config as CustomAxiosRequestConfig)._session = getRefreshToken();
            const token = getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }, error => {
            return Promise.reject(error);
        });

        this.api.interceptors.response.use((response: AxiosResponse) => {
            return response;
        }, (error: any) => {
            const originalRequest: CustomAxiosRequestConfig = error.config;

            // Frozen is checked before the 401 branch: a refresh cannot undo it,
            // and trying would only loop. (A 503 service_unavailable — the access
            // check could not reach the DB — falls through to a plain reject:
            // the session is still valid, so it must NOT log out.)
            if (isAccountFrozenError(error)) {
                this.handleAccountFrozen(originalRequest?._session);
                return Promise.reject(error);
            }

            // A 401 for a session that has since ended (logout, or someone else
            // logged in on this browser): nothing of that session is left to
            // refresh or log out, and the current session's tokens are not its own.
            if (error.response?.status === 401 && !this.isCurrentSession(originalRequest)) {
                return Promise.reject(error);
            }

            // Check if it's a 401 error and not a retry attempt
            if (error.response?.status === 401 && !originalRequest._retry) {
                if (this.isRefreshing) {
                    return new Promise((resolve, reject) => {
                        this.failedQueue.push({resolve, reject})
                    }).then(token => {
                        if (token && originalRequest.headers) { // Check if headers exist
                            originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        }
                        return this.api(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err); // Reject with the error from the queue
                    });
                }

                originalRequest._retry = true;
                this.isRefreshing = true;

                const refreshToken: string | undefined = getRefreshToken();
                if (refreshToken) {
                    return new Promise((resolve, reject) => {
                        // Deliberately use the bare `axios.post` (not `this.api`) so
                        // the refresh call bypasses this instance's interceptors —
                        // otherwise a 401 on refresh would recurse back into this
                        // same handler.
                        axios.post(`${BASE_URL}/oauth/refresh`, {}, {
                            headers: {
                                // Ensure Authorization header is set correctly
                                'Authorization': `Bearer ${refreshToken}`
                            }
                        })
                            .then(({data}) => {
                                if (getRefreshToken() !== refreshToken) {
                                    // The session changed while refreshing: this token
                                    // belongs to the old one — never install it.
                                    const stale = new Error('Session changed during token refresh');
                                    this.processQueue(stale, null);
                                    reject(stale);
                                    return;
                                }
                                setAccessToken(data.accessToken);
                                if (data.accessToken && originalRequest.headers) { // Check if headers exist
                                    originalRequest.headers['Authorization'] = 'Bearer ' + data.accessToken;
                                }
                                this.processQueue(null, data.accessToken);
                                this.flushPendingAuthEvents();
                                resolve(this.api(originalRequest)); // Retry the original request
                            })
                            .catch((err) => {
                                this.processQueue(err, null);

                                if (getRefreshToken() !== refreshToken) {
                                    // The session changed while refreshing: whatever the
                                    // answer, it is about the old session — leave the new one.
                                } else if (isAccountFrozenError(err)) {
                                    // The refresh token's account is frozen.
                                    this.handleAccountFrozen(refreshToken);
                                } else if (err.response?.status === 401) {
                                    // Server explicitly rejected the refresh token — real auth failure
                                    console.warn("Refresh token rejected by server (401). Logging out.");
                                    this.handleLogoutAndRedirect();
                                } else {
                                    // Network error, timeout, 5xx — token may still be valid, don't logout
                                    console.warn("Token refresh failed (non-auth):", err.code || err.message);
                                    this.pendingAuthEvents.push({
                                        eventType: 'TOKEN_REFRESH_NETWORK_ERROR',
                                        errorMessage: err.code || err.message || 'Unknown error',
                                    });
                                }

                                reject(err);
                            })
                            .finally(() => { // Changed from .then() to .finally()
                                this.isRefreshing = false;
                            });
                    });
                } else {
                    console.error("No refresh token found.");
                    this.handleLogoutAndRedirect(); // Clear data and trigger redirect
                    return Promise.reject(error); // Reject the original request
                }
            }

            // For non-401 errors or retried 401s, reject immediately
            return Promise.reject(error);
        });
    }
}
